"use client";

import React from "react";
import { ArrowRight, Microscope, Wrench, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const services = [{
  title: "Application Consultation",
  slug: "/ApplicationConsultationPage",
  badge: "Expert Advisory",
  description: "Work directly with our spectroscopy and analytical specialists to evaluate sample feasibility, refine methods, and specify the right instrumentation for your workflow.",
  highlights: ["Method development & feasibility assessment", "Process analytical technology (PAT) integration", "Custom workflow & sampling configuration"],
  icon: Microscope,
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/application_consultation_service.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/application_consultation_service.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/application_consultation_service.png-wvc-sizes",
  alt: "Laboratory specialist performing analytical application consultation"
}, {
  title: "Preventive Maintenance & Repairs",
  slug: "/PreventiveMaintenanceAndRepairsPage",
  badge: "Lifecycle Care",
  description: "Maintain peak spectrometer precision and minimize unexpected downtime with scheduled maintenance protocols, OEM parts, and rapid engineer repair services.",
  highlights: ["Scheduled calibration & optical verification", "Emergency breakdown & fast on-site repair", "Full audit trail & qualification documentation"],
  icon: Wrench,
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/preventive_maintenance_service.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/preventive_maintenance_service.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/preventive_maintenance_service.png-wvc-sizes",
  alt: "Field engineer conducting maintenance and optical diagnostics on laboratory analyzer"
}];
export default function RelatedServices() {
  return <section id="relatedservices" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="page-container">
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3">
            Complementary Solutions
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground font-default">
            Related Technical Services
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground font-default">
            Explore specialized advisory and instrument support services tailored to keep your regulated analytical workflows compliant, optimized, and dependable.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {services.map((service, index) => {
          const IconComponent = service.icon;
          return <Card key={service.slug} data-index={index} className="group flex flex-col justify-between border border-border bg-card shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden">
                <div>
                  <div className="relative aspect-16/9 w-full overflow-hidden bg-muted">
                    <img src={service.imageSrc} data-wvc-srcset={service.imageSrcset} data-wvc-sizes={service.imageSizes} alt={service.alt} loading="lazy" decoding="async" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    <div className="absolute top-4 left-4">
                      <span className="inline-flex items-center gap-1.5 rounded-md bg-background/95 px-3 py-1 text-xs font-semibold text-foreground shadow-xs backdrop-blur-xs border border-border">
                        <IconComponent className="size-3.5 text-primary" />
                        {service.badge}
                      </span>
                    </div>
                  </div>

                  <CardHeader className="pt-6">
                    <CardTitle className="text-xl md:text-2xl font-bold text-card-foreground font-default">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-sm md:text-base text-muted-foreground mt-2 leading-relaxed font-default">
                      {service.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-2.5 pt-0">
                    <div className="text-xs font-semibold tracking-wider uppercase text-muted-foreground mb-3">
                      Service Highlights
                    </div>
                    {service.highlights.map((highlight, hIndex) => <div key={hIndex} data-index={hIndex} className="flex items-start gap-2.5 text-sm text-card-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{highlight}</span>
                      </div>)}
                  </CardContent>
                </div>

                <CardFooter className="pt-6 border-t border-border mt-6">
                  <Button asChild className="w-full sm:w-auto" variant="default" size="default">
                    <Link to={service.slug} className="flex items-center gap-2">
                      <span>Learn about {service.title}</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}